/**
 * Navbar Component
 * Navigation bar for sections
 */

import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTelegram } from '../hooks/useTelegram';

const NavbarContainer = styled.nav`
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color);
`;

const NavbarContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-lg) var(--spacing-xl);
  max-width: 600px;
  margin: 0 auto;
`;

const NavbarTitle = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.h1`
  font-size: var(--font-xl);
  font-weight: var(--font-bold);
  color: var(--text-primary);
`;

const Subtitle = styled.span`
  font-size: var(--font-sm);
  color: var(--text-secondary);
  margin-top: 2px;
`;

const NavbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
`;

const IconButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-full);
  background: transparent;
  color: var(--text-primary);
  font-size: 18px;
  transition: all var(--transition);

  &:active {
    background: var(--bg-secondary);
  }
`;

interface NavbarProps {
  title: string;
  subtitle?: string;
  showBack?: boolean;
  actions?: React.ReactNode;
}

const Navbar: React.FC<NavbarProps> = ({ title, subtitle, showBack = false, actions }) => {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();

  const handleBack = () => {
    hapticFeedback('light');
    navigate(-1);
  };

  return (
    <NavbarContainer>
      <NavbarContent>
        {showBack && (
          <IconButton onClick={handleBack}>
            ←
          </IconButton>
        )}
        <NavbarTitle>
          <Title>{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </NavbarTitle>
        <NavbarActions>
          {actions}
        </NavbarActions>
      </NavbarContent>
    </NavbarContainer>
  );
};

export default Navbar;
